import React, { useState, useEffect, useRef } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  FolderPlus,
  Save,
  X,
  AlertCircle,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { useAuth } from '../../AuthContext';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { getDefaultCategories, getCategoriesByType } from '../../utils/budgeting/categoryDefinitions';

const CategoryManager = ({ onClose, onCategoryAdded }) => {
  const { currentUser } = useAuth();
  const [categories, setCategories] = useState([]);
  const [customGroups, setCustomGroups] = useState([]);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    group: '',
    subgroup: '',
    type: 'expense',
    isCustom: true
  });
  const [newGroupName, setNewGroupName] = useState('');
  const [editingGroup, setEditingGroup] = useState(null);
  const [editGroupName, setEditGroupName] = useState('');
  const [showManageGroups, setShowManageGroups] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    loadCategories();
  }, [currentUser]);

  const loadCategories = async () => {
    try {
      setLoading(true);

      // Load default categories
      const defaults = getDefaultCategories();

      // Load all Firestore categories (custom + overridden defaults)
      const categoriesRef = collection(db, `users/${currentUser.uid}/categories`);
      const snapshot = await getDocs(categoriesRef);

      // Create a map of Firestore categories by ID
      const firestoreMap = {};
      snapshot.forEach(doc => {
        firestoreMap[doc.id] = {
          id: doc.id,
          ...doc.data()
        };
      });

      // Merge defaults with Firestore overrides
      const allCategories = [];
      const defaultIds = new Set();

      // First, process defaults and apply any overrides
      defaults.forEach(defaultCat => {
        defaultIds.add(defaultCat.id);
        if (firestoreMap[defaultCat.id]) {
          // Override exists - use Firestore version
          allCategories.push({
            ...defaultCat,
            ...firestoreMap[defaultCat.id],
            isOverridden: true
          });
        } else {
          // No override - use default
          allCategories.push(defaultCat);
        }
      });

      // Then, add purely custom categories (not overrides of defaults)
      Object.values(firestoreMap).forEach(cat => {
        if (!defaultIds.has(cat.id)) {
          allCategories.push({
            ...cat,
            isCustom: true,
            isDefault: false
          });
        }
      });

      // Sort by group, then sortOrder
      allCategories.sort((a, b) => {
        if (a.group !== b.group) return a.group.localeCompare(b.group);
        return (a.sortOrder || 999) - (b.sortOrder || 999);
      });

      setCategories(allCategories);

      // Extract ALL unique groups (from all categories - default and custom)
      const allUniqueGroups = [...new Set(allCategories.map(c => c.group))];

      // Identify which groups have at least one custom category (for display purposes)
      const customCats = allCategories.filter(c => c.isCustom && !c.isDefault);
      const groupsWithCustomCats = [...new Set(customCats.map(c => c.group))];

      // Store all unique groups for management
      setCustomGroups(allUniqueGroups);

    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.group) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const newCategory = {
        name: formData.name,
        group: formData.group,
        subgroup: formData.subgroup || null,
        type: formData.type,
        isCustom: true,
        isDefault: false,
        sortOrder: 999,
        createdAt: new Date()
      };

      const categoriesRef = collection(db, `users/${currentUser.uid}/categories`);
      const docRef = await addDoc(categoriesRef, newCategory);

      // Reload categories
      await loadCategories();

      // Reset form
      setFormData({
        name: '',
        group: '',
        subgroup: '',
        type: 'expense',
        isCustom: true
      });
      setShowAddCategory(false);

      if (onCategoryAdded) {
        onCategoryAdded();
      }

    } catch (error) {
      console.error('Error adding category:', error);
      alert('Failed to add category');
    }
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();

    if (!editingCategory) {
      alert('No category selected for editing');
      return;
    }

    // Warn if changing type (income/expense/savings)
    if (editingCategory.type !== formData.type) {
      const confirmChange = window.confirm(
        `Warning: Changing category type from "${editingCategory.type}" to "${formData.type}" may affect budget calculations.\n\nDo you want to continue?`
      );
      if (!confirmChange) {
        return;
      }
    }

    try {
      const updates = {
        name: formData.name,
        group: formData.group,
        subgroup: formData.subgroup || null,
        type: formData.type,
        isCustom: editingCategory.isCustom || false,
        isDefault: editingCategory.isDefault || false,
        isOverridden: !editingCategory.isCustom, // Mark if we're overriding a default
        updatedAt: new Date()
      };

      const categoryRef = doc(db, `users/${currentUser.uid}/categories`, editingCategory.id);

      // Use setDoc with merge for both custom and default categories
      // This creates the doc if it doesn't exist (for defaults) or updates if it does (for custom)
      await setDoc(categoryRef, updates, { merge: true });

      await loadCategories();

      setEditingCategory(null);
      setFormData({
        name: '',
        group: '',
        subgroup: '',
        type: 'expense',
        isCustom: true
      });
      setShowAddCategory(false);

      if (onCategoryAdded) {
        onCategoryAdded();
      }

    } catch (error) {
      console.error('Error updating category:', error);
      alert('Failed to update category');
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!category.isCustom) {
      alert('Default categories cannot be deleted');
      return;
    }

    if (!window.confirm(`Delete category "${category.name}"?`)) {
      return;
    }

    try {
      const categoryRef = doc(db, `users/${currentUser.uid}/categories`, category.id);
      await deleteDoc(categoryRef);
      await loadCategories();

      if (onCategoryAdded) {
        onCategoryAdded();
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category');
    }
  };

  const handleEditCategory = (category) => {
    console.log('[CategoryManager] Edit button clicked for category:', category);
    console.log('[CategoryManager] Category isCustom flag:', category.isCustom);

    // Allow editing both default and custom categories
    console.log('[CategoryManager] Setting editing state...');
    setEditingCategory(category);

    const newFormData = {
      name: category.name,
      group: category.group,
      subgroup: category.subgroup || '',
      type: category.type,
      isCustom: category.isCustom || false
    };
    console.log('[CategoryManager] Form data to be set:', newFormData);
    setFormData(newFormData);

    console.log('[CategoryManager] Opening form modal...');
    setShowAddCategory(true);
    console.log('[CategoryManager] Edit setup complete');

    // Scroll to form after state updates
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        console.log('[CategoryManager] Scrolled to form');
      }
    }, 100);
  };

  const handleAddGroup = () => {
    if (!newGroupName.trim()) {
      alert('Please enter a group name');
      return;
    }

    if (!customGroups.includes(newGroupName)) {
      setCustomGroups([...customGroups, newGroupName]);
    }

    setFormData({ ...formData, group: newGroupName });
    setNewGroupName('');
    setShowAddGroup(false);
  };

  const handleRenameGroup = async (oldGroupName, newGroupName) => {
    if (!newGroupName.trim()) {
      alert('Please enter a new group name');
      return;
    }

    if (oldGroupName === newGroupName) {
      setEditingGroup(null);
      setEditGroupName('');
      return;
    }

    try {
      // Find ALL categories with this group (both custom and default)
      const categoriesWithGroup = categories.filter(
        cat => cat.group === oldGroupName
      );

      if (categoriesWithGroup.length === 0) {
        // Just update the customGroups state if no categories use it
        setCustomGroups(customGroups.map(g => g === oldGroupName ? newGroupName : g));
        setEditingGroup(null);
        setEditGroupName('');
        return;
      }

      // Update ALL categories with this group name in Firestore
      // Use setDoc with merge to handle both custom and default categories
      const updates = categoriesWithGroup.map(cat => {
        const categoryRef = doc(db, `users/${currentUser.uid}/categories`, cat.id);
        return setDoc(categoryRef, {
          ...cat,
          group: newGroupName,
          isOverridden: !cat.isCustom, // Mark as overridden if it's a default
          updatedAt: new Date()
        }, { merge: true });
      });

      await Promise.all(updates);
      await loadCategories();

      setEditingGroup(null);
      setEditGroupName('');

      if (onCategoryAdded) {
        onCategoryAdded();
      }
    } catch (error) {
      console.error('Error renaming group:', error);
      alert('Failed to rename group');
    }
  };

  const handleDeleteGroup = async (groupName) => {
    // Check if ANY categories use this group (default or custom)
    const categoriesWithGroup = categories.filter(
      cat => cat.group === groupName
    );

    if (categoriesWithGroup.length > 0) {
      alert(
        `Cannot delete group "${groupName}" because ${categoriesWithGroup.length} categor${categoriesWithGroup.length === 1 ? 'y uses' : 'ies use'} it.\n\n` +
        `Please reassign or delete those categories first:\n${categoriesWithGroup.map(c => `- ${c.name}`).join('\n')}`
      );
      return;
    }

    if (!window.confirm(`Delete group "${groupName}"?`)) {
      return;
    }

    // Just remove from customGroups state
    setCustomGroups(customGroups.filter(g => g !== groupName));
  };

  const handleMoveCategory = async (category, direction) => {
    try {
      // Find all categories in the same group
      const groupCategories = categories.filter(c => c.group === category.group);
      const currentIndex = groupCategories.findIndex(c => c.id === category.id);

      if (currentIndex === -1) return;

      // Check bounds
      if (direction === 'up' && currentIndex === 0) return;
      if (direction === 'down' && currentIndex === groupCategories.length - 1) return;

      // Swap sort orders
      const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      const currentCat = groupCategories[currentIndex];
      const swapCat = groupCategories[swapIndex];

      const currentSort = currentCat.sortOrder || currentIndex;
      const swapSort = swapCat.sortOrder || swapIndex;

      // Update both categories in Firestore
      const currentRef = doc(db, `users/${currentUser.uid}/categories`, currentCat.id);
      const swapRef = doc(db, `users/${currentUser.uid}/categories`, swapCat.id);

      // For default categories, we need to create overrides with sortOrder
      await setDoc(currentRef, {
        ...currentCat,
        sortOrder: swapSort,
        updatedAt: new Date()
      }, { merge: true });

      await setDoc(swapRef, {
        ...swapCat,
        sortOrder: currentSort,
        updatedAt: new Date()
      }, { merge: true });

      await loadCategories();
    } catch (error) {
      console.error('Error reordering category:', error);
      alert('Failed to reorder category');
    }
  };

  // Get all unique groups (default + custom)
  const allGroups = [...new Set([
    ...categories.map(c => c.group),
    ...customGroups
  ])].sort();

  // Group categories by group
  const groupedCategories = {};
  categories.forEach(cat => {
    if (!groupedCategories[cat.group]) {
      groupedCategories[cat.group] = [];
    }
    groupedCategories[cat.group].push(cat);
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Manage Categories</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Create custom categories or use default ones
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Add Category Button */}
          <div className="mb-6 flex gap-3">
            <button
              onClick={() => {
                setEditingCategory(null);
                setFormData({
                  name: '',
                  group: '',
                  subgroup: '',
                  type: 'expense',
                  isCustom: true
                });
                setShowAddCategory(!showAddCategory);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Custom Category
            </button>

            <button
              onClick={() => setShowAddGroup(!showAddGroup)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <FolderPlus className="w-4 h-4" />
              Add Group
            </button>

            {customGroups.length > 0 && (
              <button
                onClick={() => setShowManageGroups(!showManageGroups)}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                Manage Groups
              </button>
            )}
          </div>

          {/* Add Group Form */}
          {showAddGroup && (
            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h3 className="font-medium text-gray-900 dark:text-white mb-3">Create New Group</h3>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="e.g., My Custom Group"
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleAddGroup}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Create
                </button>
                <button
                  onClick={() => {
                    setShowAddGroup(false);
                    setNewGroupName('');
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Manage Groups Section */}
          {showManageGroups && customGroups.length > 0 && (
            <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
              <h3 className="font-medium text-gray-900 dark:text-white mb-3">Manage All Groups</h3>
              <div className="space-y-2">
                {customGroups.map(groupName => {
                  // Count ALL categories in this group (default and custom)
                  const categoriesInGroup = categories.filter(
                    cat => cat.group === groupName
                  );

                  return (
                    <div
                      key={groupName}
                      className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg"
                    >
                      {editingGroup === groupName ? (
                        <div className="flex-1 flex gap-3">
                          <input
                            type="text"
                            value={editGroupName}
                            onChange={(e) => setEditGroupName(e.target.value)}
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                            autoFocus
                          />
                          <button
                            onClick={() => handleRenameGroup(groupName, editGroupName)}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                          >
                            <Save className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setEditingGroup(null);
                              setEditGroupName('');
                            }}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {groupName}
                              </span>
                              <span className="px-2 py-0.5 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 rounded">
                                {categoriesInGroup.length} categor{categoriesInGroup.length === 1 ? 'y' : 'ies'}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => {
                                setEditingGroup(groupName);
                                setEditGroupName(groupName);
                              }}
                              className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                              title="Rename group"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteGroup(groupName)}
                              className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                              title="Delete group"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-purple-700 dark:text-purple-300 mt-3">
                💡 Renaming a group updates ALL categories in that group (both default and custom). You can only delete empty groups.
              </p>
            </div>
          )}

          {/* Add/Edit Category Form */}
          {showAddCategory && (
            <div
              ref={formRef}
              className="mb-6 p-4 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg"
            >
              {console.log('[CategoryManager] Rendering form. Editing:', editingCategory?.name, 'Form data:', formData)}
              <h3 className="font-medium text-gray-900 dark:text-white mb-4">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </h3>
              <form onSubmit={editingCategory ? handleUpdateCategory : handleAddCategory} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Category Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Category Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Pet Food, Gaming"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  {/* Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Type *
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="income">Income</option>
                      <option value="expense">Expense</option>
                      <option value="savings">Savings</option>
                    </select>
                  </div>

                  {/* Group */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Group *
                    </label>
                    <select
                      value={formData.group}
                      onChange={(e) => setFormData({ ...formData, group: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select a group</option>
                      {allGroups.map(group => (
                        <option key={group} value={group}>{group}</option>
                      ))}
                    </select>
                  </div>

                  {/* Subgroup (optional) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Subgroup (Optional)
                    </label>
                    <input
                      type="text"
                      value={formData.subgroup}
                      onChange={(e) => setFormData({ ...formData, subgroup: e.target.value })}
                      placeholder="e.g., Pets, Entertainment"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex gap-3">
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    {editingCategory ? 'Update' : 'Add'} Category
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddCategory(false);
                      setEditingCategory(null);
                      setFormData({
                        name: '',
                        group: '',
                        subgroup: '',
                        type: 'expense',
                        isCustom: true
                      });
                    }}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Info Box */}
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900 dark:text-blue-100">
              <p className="font-medium mb-1">About Categories:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-800 dark:text-blue-200">
                <li>All categories can now be edited and renamed</li>
                <li>Use ↑↓ buttons to reorder categories within each group</li>
                <li>Default categories (grey) can be customized - only custom categories (blue) can be deleted</li>
                <li>Changes to default categories are saved as overrides</li>
              </ul>
            </div>
          </div>

          {/* Categories List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedCategories).map(([groupName, cats]) => (
                <div key={groupName}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    {groupName}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {cats.map((cat, idx) => {
                      const isFirst = idx === 0;
                      const isLast = idx === cats.length - 1;

                      return (
                        <div
                          key={cat.id || `${cat.group}-${cat.name}`}
                          className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:shadow-sm transition-shadow"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {cat.name}
                              </span>
                              {cat.isCustom ? (
                                <span className="px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded">
                                  Custom
                                </span>
                              ) : cat.isOverridden ? (
                                <span className="px-2 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 rounded">
                                  Modified
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded">
                                  Default
                                </span>
                              )}
                            </div>
                            {cat.subgroup && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {cat.subgroup}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {/* Reorder buttons */}
                            <button
                              onClick={() => handleMoveCategory(cat, 'up')}
                              disabled={isFirst}
                              className={`p-1.5 rounded transition-colors ${
                                isFirst
                                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-600'
                              }`}
                              title="Move up"
                            >
                              <ChevronUp className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleMoveCategory(cat, 'down')}
                              disabled={isLast}
                              className={`p-1.5 rounded transition-colors ${
                                isLast
                                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-600'
                              }`}
                              title="Move down"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </button>

                            {/* Edit button - now for all categories */}
                            <button
                              onClick={(e) => {
                                console.log('[CategoryManager] Button clicked, event:', e);
                                handleEditCategory(cat);
                              }}
                              className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                              title="Edit category"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>

                            {/* Delete button - only for custom categories */}
                            {cat.isCustom && (
                              <button
                                onClick={() => handleDeleteCategory(cat)}
                                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                title="Delete category"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default CategoryManager;
